import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Play, Settings, BarChart3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import Navbar from '../../components/layout/Navbar';

const steps = [
  {
    title: 'Platform Setup & Onboarding',
    icon: Settings,
    description: 'Get your account configured and ready for success',
    content: [
      'Create your account and verify your email address',
      'Complete the onboarding questionnaire to personalize your experience',
      'Set up your profile with business information and preferences',
      'Choose your subscription plan based on your needs',
      'Explore the main dashboard and familiarize yourself with the interface'
    ]
  },
  {
    title: 'Creating Your First Campaign',
    icon: Play,
    description: 'Launch your first AI-powered content campaign',
    content: [
      'Navigate to Ad Studio and upload your first product image',
      'Select your campaign objective (product showcase, lifestyle, etc.)',
      'Configure AI settings including style preferences and brand guidelines',
      'Generate multiple variations and select your favorites',
      'Review and approve your generated content before publishing'
    ]
  },
  {
    title: 'Connecting Social Accounts',
    icon: CheckCircle,
    description: 'Link your social media platforms for seamless publishing',
    content: [
      'Go to Social Connections in your dashboard',
      'Connect Facebook, Instagram, X, LinkedIn, and other platforms',
      'Authorize permissions for posting and analytics access',
      'Test connections by scheduling a sample post',
      'Set up posting preferences for each platform'
    ]
  },
  {
    title: 'Understanding the Dashboard',
    icon: BarChart3,
    description: 'Master your analytics and performance insights',
    content: [
      'Learn to read your Social Health Score and what it means',
      'Understand engagement metrics and audience growth data',
      'Set up performance goals and tracking benchmarks',
      'Explore sentiment analysis and audience insights',
      'Configure alerts and notifications for important metrics'
    ]
  }
];

const GettingStartedGuide: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Navbar />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/resources" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Getting Started Guide
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Follow these step-by-step instructions to set up your account and launch your first successful social media campaigns.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="bg-white/70 backdrop-blur-sm border border-gray-200/60 shadow-lg">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-gray-900">
                          Step {index + 1}: {step.title}
                        </CardTitle>
                        <CardDescription className="text-gray-600">
                          {step.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {step.content.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Next Steps */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">Ready to Create Amazing Content?</h2>
            <p className="text-blue-100 mb-6">
              Now that you're set up, start creating your first AI-powered social media content.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/app/content-creation/ad-studio">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                  Launch Ad Studio
                </Button>
              </Link>
              <Link to="/resources">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Explore More Guides
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GettingStartedGuide;